/**
 * Build-time feature flag for the Media Library real-response-shape fix (EW-1280).
 *
 * WHY ITS OWN FILE (mirrors LinkedWheels/featureFlag.js, gradientBuilder/
 * featureFlag.js): kept local so it can be reviewed/merged independently of the
 * parallel Hail-Mary branches that also touch src/constants. At merge this can
 * fold into src/constants/featureFlags.js.
 *
 * THE BUG (measured, NEMESIS nemz24): the standalone /media-library page unwrapped
 * `GET api/media/` at the wrong depth — `res.data.data.data.data` is `undefined`,
 * so it fell back to `res.data.data` = `{types:{…}}`, and `normalizeMediaResponse`
 * then iterated the 5 category KEYS (images/videos/audios/documents/others) as if
 * they were files, rendering 5 phantom "0 MB" rows instead of the user's real
 * uploads — AND making the empty state unreachable (the 5 keys always yield 5
 * "items", even for a zero-upload account).
 *
 * ON  → unwrap `res.data.data.types` and flatten each category's real `.data[]`
 *       array of files (name/size/type/thumbnail) via `normalizeMediaTypes`.
 * OFF → the exact legacy unwrap + `normalizeMediaResponse` run unchanged, so
 *       behaviour is byte-identical to today.
 *
 * DISPLAY-ONLY: Delete / Move / New-Folder stay the safe client-only no-ops they
 * are today (EW-1281 / EW-1282). Wiring real deletion is a separate product-risk
 * decision and is deliberately NOT touched here.
 *
 * Defaults OFF; flipped per environment via REACT_APP_MEDIA_LIBRARY_REAL_SHAPE.
 */
const truthy = (v) => v === "true" || v === "1" || v === true;

export const MEDIA_LIBRARY_REAL_SHAPE = truthy(
  process.env.REACT_APP_MEDIA_LIBRARY_REAL_SHAPE
);

/**
 * EW-1281 — wire the Media Library Delete control to the real backend.
 *
 * THE BUG (measured, NEMESIS nemz24): on /media-library, confirming the delete
 * modal ("This can't be undone / Delete everywhere") fired NO `api/media/delete/`
 * request. `DeleteMediaModal` onConfirm was a client-only no-op
 * (`setDeleteItems(null); setSelectedIds([]); invalidateMedia();`) — the invalidate
 * re-fetched and the item reappeared. `deleteMediaFiles` (`api/media/delete/`)
 * already exists in services/api.js but was never called. A destructive-worded
 * control performed no deletion.
 *
 * ON  → onConfirm runs a react-query mutation that POSTs the selected files'
 *       storage `path`s to `deleteMediaFiles`, then invalidates the media query so
 *       the deleted files disappear. Double-submit-guarded via the mutation's
 *       in-flight state; a failed delete keeps the modal open with an error.
 * OFF → the exact legacy client-only no-op runs unchanged (byte-identical) — the
 *       button remains a safe no-op, no destructive request is ever sent.
 *
 * WHY FLAGGED (base OFF): this turns a harmless no-op into a REAL, irreversible
 * DELETE of the user's files. Gated so prod keeps the safe no-op until QA-verified;
 * "true" only for staging. Meaningfully deletes only when REAL_SHAPE is also ON
 * (phantom rows carry no `path`, so nothing is ever sent for them).
 *
 * Defaults OFF; flipped per environment via REACT_APP_MEDIA_LIBRARY_DELETE.
 */
export const MEDIA_LIBRARY_DELETE = truthy(
  process.env.REACT_APP_MEDIA_LIBRARY_DELETE
);

/**
 * gordon-0831-media-library-rename — /media-library had no way to rename a
 * file at all (measured live on web-stg: grid tiles offer only select/play/
 * expand; the list view's "More actions" kebab is the EW-1284 dead stub —
 * suppressed via `showKebab={false}` and, even shown, only ran
 * `e.stopPropagation()`). BE now exposes `POST api/media/rename/`
 * (`renameMedia` in services/api.js), storing a per-user display-name
 * override keyed by the file's S3 path — the file's real key/URL never
 * changes, so nothing already referencing it breaks.
 *
 * ON  → both MediaGrid tiles and MediaListTable rows get a real "Rename"
 *       control (a distinct affordance, NOT the old dead kebab) that opens
 *       RenamePaletteDialog and, on confirm, calls renameMedia then
 *       invalidates the media query so the new name shows immediately.
 * OFF → no rename control renders anywhere — byte-identical to today.
 *
 * Defaults OFF; flipped per environment via REACT_APP_MEDIA_LIBRARY_RENAME.
 */
export const MEDIA_LIBRARY_RENAME = truthy(
  process.env.REACT_APP_MEDIA_LIBRARY_RENAME
);
